import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, FileText, Clock, CheckCircle2, Plus, PlusCircle, Trash2, X, Cloud } from "lucide-react";
import axios from "axios";
import { CustomQuestionsSection } from "./CustomQuestionField";
import {
  validateTimeChain,
  validateEntryTimes,
  validateEntriesWithinPeriod,
  validateNoOverlap,
  validateGlobalChain,
} from "../utils/timeValidations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Card individual de uma ocorrência de atividade (Seções 2 e 4) =====
const ActivityEntryCard = ({ entry, idx, fieldName, period, activityOptions, onUpdate, onRemove, errors }) => {
  const isOther = entry.activity === 'OTHER';
  const errKey = (key) => `${period}_entry_${idx}_${key}`;
  const hasError = (key) => Boolean(errors[errKey(key)]);
  return (
    <div
      className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm space-y-3"
      data-testid={`${period}-entry-card-${idx}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-slate-500 font-medium">Ocorrência #{idx + 1}</div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(fieldName, idx)}
          data-testid={`${period}-entry-remove-${idx}`}
          className="text-red-600 hover:bg-red-50 h-7"
          title="Remover atividade"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        {/* Atividade (sempre) */}
        <div className={isOther ? "md:col-span-4" : "md:col-span-8"}>
          <Label className="text-xs">Atividade *</Label>
          <Select
            value={entry.activity || ''}
            onValueChange={(v) => onUpdate(fieldName, idx, 'activity', v)}
          >
            <SelectTrigger
              data-testid={`${period}-entry-activity-${idx}`}
              className={hasError('activity') ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Selecione…" />
            </SelectTrigger>
            <SelectContent>
              {activityOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasError('activity') && <p className="text-xs text-red-500 mt-1">{errors[errKey('activity')]}</p>}
        </div>

        {/* Custom label só para OTHER */}
        {isOther && (
          <div className="md:col-span-4">
            <Label className="text-xs">Qual atividade? *</Label>
            <Input
              value={entry.custom_label || ''}
              onChange={(e) => onUpdate(fieldName, idx, 'custom_label', e.target.value)}
              placeholder="Descreva a atividade"
              data-testid={`${period}-entry-custom-label-${idx}`}
              className={hasError('custom_label') ? 'border-red-500' : ''}
            />
            {hasError('custom_label') && <p className="text-xs text-red-500 mt-1">{errors[errKey('custom_label')]}</p>}
          </div>
        )}

        {/* Início e Fim */}
        <div className="md:col-span-2">
          <Label className="text-xs">Início *</Label>
          <Input
            type="time"
            value={entry.start_time || ''}
            onChange={(e) => onUpdate(fieldName, idx, 'start_time', e.target.value)}
            data-testid={`${period}-entry-start-${idx}`}
            className={hasError('start_time') ? 'border-red-500' : ''}
          />
          {hasError('start_time') && <p className="text-xs text-red-500 mt-1">{errors[errKey('start_time')]}</p>}
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Fim *</Label>
          <Input
            type="time"
            value={entry.end_time || ''}
            onChange={(e) => onUpdate(fieldName, idx, 'end_time', e.target.value)}
            data-testid={`${period}-entry-end-${idx}`}
            className={hasError('end_time') ? 'border-red-500' : ''}
          />
          {hasError('end_time') && <p className="text-xs text-red-500 mt-1">{errors[errKey('end_time')]}</p>}
        </div>
      </div>

      {/* Comentário (opcional) */}
      <div>
        <Label className="text-xs">Comentário</Label>
        <Textarea
          value={entry.comments || ''}
          onChange={(e) => onUpdate(fieldName, idx, 'comments', e.target.value)}
          placeholder="Observações desta ocorrência (opcional)"
          rows={2}
          data-testid={`${period}-entry-comments-${idx}`}
        />
      </div>

      {/* Aviso de sobreposição com outra ocorrência (1 robô) */}
      {hasError('overlap') && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2"
          data-testid={`${period}-entry-${idx}-overlap-error`}
        >
          ⚠️ {errors[errKey('overlap')]}
        </div>
      )}
    </div>
  );
};

const RDOFormNew = ({ editingReport = null, onReportCreated }) => {
  // Estado principal do formulário
  const [formData, setFormData] = useState({
    // Cliente associado
    client_id: '',
    
    // Informações Administrativas
    report_date: new Date().toISOString().split('T')[0],
    pt_room_time: '',
    pt_issuance_time: '',
    pt_numbers: '',
    pt_approval_time: '',
    morning_start_time: '',
    
    // Atividades da Manhã
    morning_activities: [],
    morning_activity_entries: [],   // {id, activity, custom_label, start_time, end_time, comments}
    morning_other_description: '',
    morning_comments: '',
    
    // Parada de Almoço
    lunch_departure_time: '',
    lunch_return_time: '',
    
    // Atividades da Tarde
    afternoon_activities: [],
    afternoon_activity_entries: [],
    afternoon_other_description: '',
    afternoon_comments: '',
    
    // Fotos e Comentários
    progress_photo_url: '',
    
    // Dados Específicos das Atividades
    region_worked: '',
    columns_sandblasted_entirely: '',
    sandblasted_columns_width: '',
    sandblasted_columns_height: '',
    partial_columns_sandblasted: null,
    partial_sandblasted_height: '',
    sandblasting_rework: null,
    sandblasting_rework_details: '',
    robot_advance_speed: '',
    turbine_speed: '',
    turbine_speed_changed: null,
    turbine_speed_change_cav: '',
    sandblasting_current: '',
    implement_change: null,
    tool_change_time: '',
    columns_painted_entirely: '',
    painted_columns_width: '',
    painted_columns_height: '',
    partial_columns_painted: null,
    partial_painted_width: '',
    partial_painted_height: '',
    painting_hours: '',
    
    // Impactos & Impedimentos
    impediments_occurred: null,
    impediments_list: [],
    impediments_comments: '',
    impediment_resolution_time: '',
    
    // Almoxarifado & Spare Parts
    warehouse_item_moved: null,
    materials_used: [],
    movement_conditions: {},
    urgent_replacement_comments: '',
    turbine_kit_replaced: null,
    turbine_kit_balanced: null,
    sealing_kit_replaced: null,
    sealing_kits_stock: '',
    grit_used_volume: '',
    grit_loss: null,
    grit_lost_volume: '',
    grit_remaining_volume: '',
    grit_minimum_reached: null,
    grit_stock_comments: '',
    paint_used_volume: '',
    paint_remaining_volume: '',
    component_ab_used_volume: '',
    component_ab_remaining_volume: '',
    inhibitor_used_volume: '',
    inhibitor_remaining_volume: '',
    solvent_used_volume: '',
    solvent_remaining_volume: '',
    
    // Encerramento
    daily_end_time: '',
    administrative_demands: '',
    // Fase C — Respostas para perguntas customizadas (chave = question_id)
    custom_answers: {}
  });

  // Estado da navegação e controle
  const [activeTab, setActiveTab] = useState('admin');
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState({});
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showEditExistingDialog, setShowEditExistingDialog] = useState(false);
  const [impedimentTimes, setImpedimentTimes] = useState({});
  const [impedimentStartTimes, setImpedimentStartTimes] = useState({});
  const [impedimentComments, setImpedimentComments] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [materialActions, setMaterialActions] = useState({});
  const [adhocMaterialInput, setAdhocMaterialInput] = useState('');
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeClients, setActiveClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  // Blob URLs locais para preview (chave = nome do campo de foto)
  const [photoPreviews, setPhotoPreviews] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState({}); // { field: true/false }
  // Fase C — Perguntas customizadas e rascunho local opt-in
  const [customQuestions, setCustomQuestions] = useState([]);
  const [localDraftAvailable, setLocalDraftAvailable] = useState(null); // { key, savedAt }
  const [savingDraft, setSavingDraft] = useState(false);

  // Opções para as atividades
  const activityOptions = [
    { value: "RDO_ISSUANCE", label: "Emissão do Diário de Bordo - Relatório RDO" },
    { value: "CORRECTIVE_MAINTENANCE", label: "Manutenção Corretiva" },
    { value: "PREVENTIVE_MAINTENANCE", label: "Manutenção Preventiva" },
    { value: "CONTAINER_ORG_CLEANING", label: "Organização e Limpeza dos Containers" },
    { value: "WAREHOUSE_COUNT", label: "Contagem do Almoxarifado no Container" },
    { value: "SANDBLASTING", label: "Jateamento" },
    { value: "SANDBLASTING_REWORK", label: "Repassagem de Jateamento" },
    { value: "ROUGHNESS_MEASUREMENT", label: "Medição de Rugosidade" },
    { value: "CORROSION_INHIBITOR_APP", label: "Aplicação de Inibidor de Corrosão" },
    { value: "PAINTING_LAYER1", label: "Pintura - Camada 01" },
    { value: "PAINTING_LAYER2", label: "Pintura - Camada 02" },
    { value: "PAINTING_LAYER3", label: "Pintura - Camada 03" },
    { value: "THICKNESS_MEASUREMENT", label: "Medição de Espessura" },
    { value: "OTHER", label: "Outras" }
  ];

  const impedimentOptions = [
    { value: "PT_ISSUANCE", label: "Emissão de PT" },
    { value: "PT_APPROVAL", label: "Aprovação PT" },
    { value: "GANGWAY", label: "Desconexão da Gangway" },
    { value: "LOAD_MOVEMENT", label: "Movimentação de Carga" },
    { value: "SWELL_INCREASE", label: "Aumento nas Ondulações (Swell)" },
    { value: "TOOL_CHANGE_BLAST_PAINT", label: "Troca de implemento de Jato para pintura" },
    { value: "SCAFFOLDING_TEAM", label: "Atendimento da equipe de andaimes" },
    { value: "WEATHER_CONDITIONS", label: "Condições do tempo (Chuva/ vento)" },
    { value: "MAINTENANCE", label: "Manutenções" },
    { value: "OTHER", label: "Outras" }
  ];

  // Definição das abas
  const tabs = [
    { id: 'admin', label: 'Info. Administrativas', icon: '📋' },
    { id: 'morning', label: 'Atividades Manhã', icon: '🌅' },
    { id: 'lunch', label: 'Parada Almoço', icon: '🍽️' },
    { id: 'afternoon', label: 'Atividades Tarde', icon: '🌇' },
    { id: 'photos', label: 'Fotos & Comentários', icon: '📸' },
    { id: 'activities', label: 'Dados Específicos', icon: '📊' },
    { id: 'impediments', label: 'Impedimentos', icon: '⚠️' },
    { id: 'warehouse', label: 'Almoxarifado', icon: '📦' },
    { id: 'closing', label: 'Encerramento', icon: '✅' }
  ];

  // Auto-save functionality
  const autoSave = useCallback(async () => {
    if (Object.keys(formData).length === 0) return;
    
    setIsAutoSaving(true);
    try {
      const draftKey = `rdo_draft_${formData.report_date || 'current'}`;
      localStorage.setItem(draftKey, JSON.stringify({
        ...formData,
        lastSaved: new Date().toISOString()
      }));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [formData]);

  // Auto-save a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(autoSave, 30000);
    return () => clearInterval(interval);
  }, [autoSave]);

  // ==== Upload de foto para Object Storage ====
  const uploadPhoto = async (fieldName, file) => {
    if (!file) return;

    // Validação client-side
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes((file.type || '').toLowerCase())) {
      toast.error('Formato não suportado. Use JPG, PNG, WebP ou GIF.');
      return;
    }
    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_SIZE) {
      toast.error('Arquivo excede o limite de 10 MB.');
      return;
    }

    // Preview local imediato (blob URL) — revogado quando substituído/desmontado
    const localUrl = URL.createObjectURL(file);
    setPhotoPreviews((prev) => {
      const prevUrl = prev[fieldName];
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return { ...prev, [fieldName]: localUrl };
    });

    setUploadingPhoto((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post(`${API}/rdo/upload-photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const storagePath = response.data.storage_path;
      setFormData((prev) => ({ ...prev, [fieldName]: storagePath }));
      toast.success('Foto enviada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar foto');
      setPhotoPreviews((prev) => {
        const prevUrl = prev[fieldName];
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    } finally {
      setUploadingPhoto((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  // Limpar blob URLs ao desmontar
  useEffect(() => {
    return () => {
      Object.values(photoPreviews).forEach((u) => {
        if (u) URL.revokeObjectURL(u);
      });
    };
  }, []); // eslint-disable-line

  // Validar horários sempre que mudarem
  useEffect(() => {
    if (activeTab === 'admin') {
      validateTab('admin');
    }
  }, [formData.pt_room_time, formData.pt_issuance_time, formData.pt_approval_time, formData.morning_start_time, activeTab]);

  // Carregar lista de clientes ativos (EM_ANDAMENTO) para o seletor
  useEffect(() => {
    const fetchActiveClients = async () => {
      setLoadingClients(true);
      try {
        const response = await axios.get(`${API}/clients/active`);
        setActiveClients(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar clientes ativos:', error);
        toast.error('Não foi possível carregar a lista de clientes');
      } finally {
        setLoadingClients(false);
      }
    };
    fetchActiveClients();
  }, []);

  // Carregar dados se estiver editando
  useEffect(() => {
    if (editingReport) {
      // Migrar RDOs antigos: se houver morning_activities mas sem entries, converter
      const _migrateLegacyEntries = (rep, prefix) => {
        const entries = rep[`${prefix}_activity_entries`];
        if (entries && entries.length > 0) return entries;
        const legacy = rep[`${prefix}_activities`] || [];
        if (legacy.length === 0) return [];
        const otherDesc = rep[`${prefix}_other_description`] || '';
        return legacy.map(code => ({
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
          activity: code,
          custom_label: code === 'OTHER' ? otherDesc : '',
          start_time: '',
          end_time: '',
          comments: '',
        }));
      };
      const migrated = {
        ...editingReport,
        morning_activity_entries: _migrateLegacyEntries(editingReport, 'morning'),
        afternoon_activity_entries: _migrateLegacyEntries(editingReport, 'afternoon'),
      };
      setFormData(migrated);
      // Popular estados separados a partir do relatório
      if (editingReport.impediment_times) setImpedimentTimes(editingReport.impediment_times || {});
      if (editingReport.impediment_start_times) setImpedimentStartTimes(editingReport.impediment_start_times || {});
      if (editingReport.impediment_comments) setImpedimentComments(editingReport.impediment_comments || {});
      if (editingReport.selected_materials) setSelectedMaterials(editingReport.selected_materials || []);
      if (editingReport.material_actions) setMaterialActions(editingReport.material_actions || {});
    } else {
      // "Novo Relatório" sempre inicia LIMPO. O rascunho local não é mais auto-carregado.
      // Se houver rascunho salvo no navegador, exibimos um banner opt-in (handleResumeLocalDraft).
      const draftKey = `rdo_draft_${new Date().toISOString().split('T')[0]}`;
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setLocalDraftAvailable({ key: draftKey, savedAt: parsed.lastSaved });
        } catch { /* ignora rascunho corrompido */ }
      }
    }
  }, [editingReport]);

  // Carregar perguntas customizadas ativas (Fase C) — uma vez ao montar
  useEffect(() => {
    const fetchCQ = async () => {
      try {
        const res = await axios.get(`${API}/custom-questions`, { params: { only_active: true } });
        setCustomQuestions(res.data || []);
      } catch (e) {
        // Falha não bloqueia o RDO; perguntas simplesmente não aparecem
        console.error('Falha ao carregar perguntas customizadas', e);
      }
    };
    fetchCQ();
  }, []);

  // Carregar manualmente o rascunho local (banner opt-in)
  const handleResumeLocalDraft = () => {
    if (!localDraftAvailable) return;
    try {
      const raw = localStorage.getItem(localDraftAvailable.key);
      if (!raw) return;
      const draft = JSON.parse(raw);
      setFormData(draft);
      if (draft.lastSaved) setLastSaved(new Date(draft.lastSaved));
      setLocalDraftAvailable(null);
      toast.success('Rascunho local carregado');
    } catch (e) {
      toast.error('Não foi possível carregar o rascunho local');
    }
  };

  const handleDiscardLocalDraft = () => {
    if (!localDraftAvailable) return;
    localStorage.removeItem(localDraftAvailable.key);
    setLocalDraftAvailable(null);
    toast.info('Rascunho local descartado');
  };

  // Atualiza resposta de uma pergunta customizada (Fase C)
  const setCustomAnswer = (qid, value) => {
    setFormData(prev => ({
      ...prev,
      custom_answers: { ...(prev.custom_answers || {}), [qid]: value },
    }));
  };

  // Salvar rascunho no backend (cria ou atualiza RDO com submitted=False)
  const saveDraftToBackend = async () => {
    if (!formData.report_date) {
      toast.error('Informe ao menos a data do relatório antes de salvar o rascunho');
      return;
    }
    setSavingDraft(true);
    try {
      const processed = { ...formData };
      // garantir formato esperado
      processed.impediment_times = impedimentTimes || {};
      processed.impediment_start_times = impedimentStartTimes || {};
      processed.impediment_comments = impedimentComments || {};
      processed.selected_materials = selectedMaterials || [];
      processed.material_actions = materialActions || {};
      // Normalizar urgency_comments
      const urgencyComments = {};
      Object.keys(processed).forEach(k => {
        if (k.startsWith('urgency_comment_') && processed[k]) {
          urgencyComments[k.replace('urgency_comment_', '')] = processed[k];
        }
      });
      processed.material_urgency_comments = urgencyComments;
      // Derivar morning_activities/afternoon_activities das entries (mantém compat)
      const _uniqueFromEntries = (entries) => {
        const seen = [];
        (entries || []).forEach(e => { if (e?.activity && !seen.includes(e.activity)) seen.push(e.activity); });
        return seen;
      };
      processed.morning_activities = _uniqueFromEntries(processed.morning_activity_entries);
      processed.afternoon_activities = _uniqueFromEntries(processed.afternoon_activity_entries);

      if (editingReport?.id) {
        await axios.put(`${API}/reports/${editingReport.id}`, processed);
      } else {
        // Tentar criar; se já existir RDO para a data, fazer PUT no existente
        try {
          const res = await axios.post(`${API}/reports`, processed);
          // próximo save vai precisar do ID — atualiza state
          if (res.data?.id) {
            // simula edição daqui em diante pelo restante da sessão
            // (não há setState para editingReport aqui, mas autosave silencioso é via PUT na próxima vez)
          }
        } catch (e) {
          if (e.response?.status === 400 && e.response.data?.detail?.includes('já existe')) {
            // RDO da data já existe — buscar e fazer PUT
            const list = await axios.get(`${API}/reports`);
            const existing = (list.data || []).find(r => r.report_date === processed.report_date && r.user_id);
            if (existing) {
              await axios.put(`${API}/reports/${existing.id}`, processed);
            } else {
              throw e;
            }
          } else {
            throw e;
          }
        }
      }
      toast.success('Rascunho salvo no servidor');
      setLastSaved(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar rascunho');
    } finally {
      setSavingDraft(false);
    }
  };

  // Lógica condicional - verificar se jateamento está selecionado
  // Usa entries (fonte da verdade) com fallback para listas legadas
  const _activityCodes = (entries, legacyList) => {
    if (entries && entries.length > 0) return entries.map(e => e?.activity).filter(Boolean);
    return legacyList || [];
  };
  const _morningCodes = () => _activityCodes(formData.morning_activity_entries, formData.morning_activities);
  const _afternoonCodes = () => _activityCodes(formData.afternoon_activity_entries, formData.afternoon_activities);

  const isSandblastingSelected = () => {
    const m = _morningCodes(); const a = _afternoonCodes();
    return m.includes('SANDBLASTING') || m.includes('SANDBLASTING_REWORK') ||
           a.includes('SANDBLASTING') || a.includes('SANDBLASTING_REWORK');
  };

  // Lógica condicional - verificar se pintura está selecionada
  const isPaintingSelected = () => {
    const paintingActivities = ['PAINTING_LAYER1', 'PAINTING_LAYER2', 'PAINTING_LAYER3'];
    const m = _morningCodes(); const a = _afternoonCodes();
    return paintingActivities.some(activity =>
      m.includes(activity) ||
      a.includes(activity)
    );
  };

  // Lógica condicional - verificar se aplicação de inibidor está selecionada
  const isInhibitorSelected = () => {
    return _morningCodes().includes('CORROSION_INHIBITOR_APP') ||
           _afternoonCodes().includes('CORROSION_INHIBITOR_APP');
  };

  // Lógica condicional - verificar se troca de implemento deve ser habilitada
  const shouldEnableImplementChange = () => {
    const hasSandblasting = isSandblastingSelected();
    const hasPainting = isPaintingSelected();
    const hasInhibitor = isInhibitorSelected();
    return hasSandblasting && hasPainting && hasInhibitor;
  };

  // Função para verificar se existe relatório na data
  const checkExistingReport = async (date) => {
    try {
      const response = await axios.get(`${API}/reports`);
      const existingReport = response.data.find(report => report.report_date === date);
      return existingReport;
    } catch (error) {
      console.error('Erro ao verificar relatório existente:', error);
      return null;
    }
  };

  // Função para validar horários em cascata
  const validateTimeSequence = () => {
    const errors = {};
    
    // Converter horários para minutos para comparação
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return null;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const ptRoomTime = timeToMinutes(formData.pt_room_time);
    const ptIssuanceTime = timeToMinutes(formData.pt_issuance_time);
    const ptApprovalTime = timeToMinutes(formData.pt_approval_time);
    const morningStartTime = timeToMinutes(formData.morning_start_time);

    // Validar sequência de horários
    if (ptRoomTime && ptIssuanceTime && ptIssuanceTime < ptRoomTime) {
      errors.pt_issuance_time = 'Horário de emissão deve ser posterior ao horário na sala de PT';
    }

    if (ptIssuanceTime && ptApprovalTime && ptApprovalTime < ptIssuanceTime) {
      errors.pt_approval_time = 'Horário de aprovação deve ser posterior ao horário de emissão';
    }

    if (ptApprovalTime && morningStartTime && morningStartTime < ptApprovalTime) {
      errors.morning_start_time = 'Horário de início deve ser posterior ao horário de aprovação da PT';
    }

    return errors;
  };

  // Manipular mudança de data com verificação de relatório existente
  const handleDateChange = async (newDate) => {
    setFormData(prev => ({ ...prev, report_date: newDate }));
    
    // Se a data for anterior à hoje, verificar se existe relatório
    const today = new Date().toISOString().split('T')[0];
    if (newDate < today && !editingReport) {
      const existingReport = await checkExistingReport(newDate);
      if (existingReport) {
        setShowEditExistingDialog(true);
      }
    }
  };

  // Função para ir para "Meus Relatórios"
  const goToMyReports = () => {
    setShowEditExistingDialog(false);
    // Assumindo que onReportCreated pode ser usado para navegar
    if (onReportCreated) {
      onReportCreated('navigate-to-reports');
    }
  };


  // Verificar se uma aba está completa
  const isTabComplete = (tabId) => {
    switch (tabId) {
      case 'admin':
        return formData.client_id &&
               formData.report_date && 
               formData.pt_room_time && 
               formData.pt_issuance_time && 
               formData.pt_approval_time && 
               formData.morning_start_time &&
               Object.keys(validateTimeSequence()).length === 0;
      case 'lunch':
        return formData.lunch_departure_time && formData.lunch_return_time;
      default:
        return false;
    }
  };

  // Verificar se uma aba pode ser acessada
  const canAccessTab = (tabId) => {
    switch (tabId) {
      case 'morning':
        return isTabComplete('admin');
      case 'afternoon':
        return isTabComplete('lunch');
      default:
        return true;
    }
  };

  // Validação em tempo real
  const validateTab = (tabId) => {
    const errors = {};
    
    switch (tabId) {
      case 'admin':
        if (!formData.client_id) {
          errors.client_id = 'Cliente é obrigatório';
        }
        if (!formData.report_date) {
          errors.report_date = 'Data é obrigatória';
        } else if (new Date(formData.report_date) > new Date()) {
          errors.report_date = 'Data não pode ser futura';
        }
        // Cadeia cronológica administrativa
        Object.assign(errors, validateTimeChain([
          ['pt_room_time',     formData.pt_room_time,     'Horário na sala de PT'],
          ['pt_issuance_time', formData.pt_issuance_time, 'Horário de emissão da PT'],
          ['pt_approval_time', formData.pt_approval_time, 'Horário de aprovação da PT'],
          ['morning_start_time', formData.morning_start_time, 'Início das atividades da manhã'],
        ]));
        break;
      
      case 'lunch':
        if (!formData.lunch_departure_time) {
          errors.lunch_departure_time = 'Horário de saída é obrigatório';
        }
        if (!formData.lunch_return_time) {
          errors.lunch_return_time = 'Horário de volta é obrigatório';
        }
        // Volta do almoço deve ser ≥ saída
        Object.assign(errors, validateTimeChain([
          ['lunch_departure_time', formData.lunch_departure_time, 'Saída para almoço'],
          ['lunch_return_time',    formData.lunch_return_time,    'Volta do almoço'],
        ]));
        break;
      
      case 'morning':
        if (!formData.morning_activity_entries || formData.morning_activity_entries.length === 0) {
          errors.morning_activity_entries = 'Adicione pelo menos uma atividade da manhã';
        } else {
          formData.morning_activity_entries.forEach((e, idx) => {
            if (!e.activity) errors[`morning_entry_${idx}_activity`] = 'Selecione a atividade';
            if (e.activity === 'OTHER' && !e.custom_label?.trim()) errors[`morning_entry_${idx}_custom_label`] = 'Descreva qual atividade';
            if (!e.start_time) errors[`morning_entry_${idx}_start_time`] = 'Início obrigatório';
            if (!e.end_time) errors[`morning_entry_${idx}_end_time`] = 'Fim obrigatório';
          });
          // Validações cronológicas:
          // a) end_time ≥ start_time em cada entry
          Object.assign(errors, validateEntryTimes(formData.morning_activity_entries, 'morning'));
          // b) Entries dentro do período manhã (≥ morning_start_time, ≤ lunch_departure_time)
          Object.assign(errors, validateEntriesWithinPeriod(
            formData.morning_activity_entries,
            formData.morning_start_time,
            formData.lunch_departure_time,
            'morning',
            'Início das atividades da manhã',
            'Saída para almoço'
          ));
          // c) Sem sobreposição (1 robô)
          Object.assign(errors, validateNoOverlap(formData.morning_activity_entries, 'morning'));
        }
        break;
      
      case 'afternoon':
        if (!formData.afternoon_activity_entries || formData.afternoon_activity_entries.length === 0) {
          errors.afternoon_activity_entries = 'Adicione pelo menos uma atividade da tarde';
        } else {
          formData.afternoon_activity_entries.forEach((e, idx) => {
            if (!e.activity) errors[`afternoon_entry_${idx}_activity`] = 'Selecione a atividade';
            if (e.activity === 'OTHER' && !e.custom_label?.trim()) errors[`afternoon_entry_${idx}_custom_label`] = 'Descreva qual atividade';
            if (!e.start_time) errors[`afternoon_entry_${idx}_start_time`] = 'Início obrigatório';
            if (!e.end_time) errors[`afternoon_entry_${idx}_end_time`] = 'Fim obrigatório';
          });
          Object.assign(errors, validateEntryTimes(formData.afternoon_activity_entries, 'afternoon'));
          Object.assign(errors, validateEntriesWithinPeriod(
            formData.afternoon_activity_entries,
            formData.lunch_return_time,
            formData.daily_end_time,
            'afternoon',
            'Volta do almoço',
            'Encerramento do dia'
          ));
          Object.assign(errors, validateNoOverlap(formData.afternoon_activity_entries, 'afternoon'));
        }
        break;
      
      case 'activities':
        if (!formData.region_worked) {
          errors.region_worked = 'Região (CAV) é obrigatória';
        }
        break;

      case 'impediments':
        // Se "Outras" foi selecionado, seu comentário individual é obrigatório
        if (formData.impediments_list?.includes('OTHER') && !impedimentComments.OTHER?.trim()) {
          errors.comment_OTHER = 'Comentário é obrigatório para "Outras"';
        }
        break;

      case 'closing':
        // Encerramento ≥ último Fim das atividades da tarde
        if (formData.daily_end_time && (formData.afternoon_activity_entries || []).length > 0) {
          // Pega o maior end_time da tarde
          const ends = formData.afternoon_activity_entries
            .map(e => e?.end_time)
            .filter(Boolean);
          if (ends.length > 0) {
            const lastEnd = ends.sort().slice(-1)[0];
            Object.assign(errors, validateTimeChain([
              ['__last_afternoon_end__', lastEnd, 'Fim da última atividade da tarde'],
              ['daily_end_time', formData.daily_end_time, 'Encerramento do dia'],
            ]));
          }
        }
        break;
    }

    // ===== Validação genérica de Perguntas Customizadas required (Fase C) =====
    // Mapa tab→target_section (incluindo demands renderizada na aba closing)
    const tabSectionMap = {
      admin: ['admin'],
      morning: ['morning'],
      lunch: ['lunch'],
      afternoon: ['afternoon'],
      photos: ['photos'],
      activities: ['activities'],
      impediments: ['impediments'],
      warehouse: ['warehouse'],
      closing: ['closing', 'demands'],
    };
    const sectionsForTab = tabSectionMap[tabId] || [];
    const cqErrors = {};
    sectionsForTab.forEach(sec => {
      (customQuestions || []).filter(q => q.is_active !== false && q.target_section === sec && q.is_required).forEach(q => {
        const v = formData.custom_answers?.[q.id];
        const isEmpty = v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
        if (isEmpty) {
          cqErrors[q.id] = 'Resposta obrigatória';
        }
      });
    });
    sectionsForTab.forEach(sec => {
      validationErrors[`cq_${sec}`] = {};
    });
    if (Object.keys(cqErrors).length > 0) {
      // Distribuir erros para a primeira seção mapeada (suficiente — usuário verá em cq-card)
      sectionsForTab.forEach(sec => {
        const subset = {};
        (customQuestions || []).filter(q => q.target_section === sec).forEach(q => {
          if (cqErrors[q.id]) subset[q.id] = cqErrors[q.id];
        });
        if (Object.keys(subset).length > 0) {
          errors[`__cq_${sec}__`] = `Existem perguntas customizadas obrigatórias não respondidas`;
        }
      });
    }

    setValidationErrors(prev => {
      const next = { ...prev, [tabId]: errors };
      sectionsForTab.forEach(sec => {
        const subset = {};
        (customQuestions || []).filter(q => q.target_section === sec).forEach(q => {
          if (cqErrors[q.id]) subset[q.id] = cqErrors[q.id];
        });
        next[`cq_${sec}`] = subset;
      });
      return next;
    });
    return Object.keys(errors).length === 0;
  };

  // ===== Helpers para entries de atividade (Seções 2 e 4) =====
  // Cada entry: {id, activity, custom_label, start_time, end_time, comments}
  const _newActivityEntry = () => ({
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    activity: '',
    custom_label: '',
    start_time: '',
    end_time: '',
    comments: '',
  });

  const addActivityEntry = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), _newActivityEntry()] }));
  };

  const updateActivityEntry = (field, idx, key, value) => {
    setFormData(prev => {
      const list = [...(prev[field] || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...prev, [field]: list };
    });
  };

  const removeActivityEntry = (field, idx) => {
    setFormData(prev => ({ ...prev, [field]: (prev[field] || []).filter((_, i) => i !== idx) }));
  };

  // Manipular mudança de atividades
  const handleActivityChange = (field, value, checked) => {
    const newActivities = checked 
      ? [...formData[field], value]
      : formData[field].filter(item => item !== value);
    
    setFormData(prev => ({
      ...prev,
      [field]: newActivities
    }));
  };

  // Manipular mudança de impedimentos
  const handleImpedimentChange = (value, checked) => {
    const newImpediments = checked 
      ? [...formData.impediments_list, value]
      : formData.impediments_list.filter(item => item !== value);
    
    setFormData(prev => ({
      ...prev,
      impediments_list: newImpediments
    }));
  };

  // Navegação entre abas
  const handleTabChange = (tabId) => {
    if (!canAccessTab(tabId)) {
      toast.warning("Complete a aba anterior antes de prosseguir");
      return;
    }
    validateTab(activeTab);
    setActiveTab(tabId);
  };

  const nextTab = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      const isValid = validateTab(activeTab);
      if (isValid) {
        setCompletedTabs(prev => new Set([...prev, activeTab]));
        setActiveTab(tabs[currentIndex + 1].id);
      }
    }
  };

  const prevTab = () => {
    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  // Submeter formulário
  const handleSubmitClick = () => {
    // Validar todas as abas antes de mostrar o dialog
    let allValid = true;
    tabs.forEach(tab => {
      if (!validateTab(tab.id)) {
        allValid = false;
      }
    });

    if (!allValid) {
      toast.error("Por favor, corrija os erros antes de submeter");
      return;
    }

    // Validação cronológica GLOBAL (cross-aba) — só executa no submit final
    const globalCheck = validateGlobalChain(formData);
    if (!globalCheck.valid) {
      // Distribuir erros nas abas afetadas para exibição inline
      setValidationErrors(prev => {
        const next = { ...prev };
        Object.entries(globalCheck.errors).forEach(([tabId, fieldErrs]) => {
          next[tabId] = { ...(next[tabId] || {}), ...fieldErrs };
        });
        return next;
      });
      // Mensagem detalhada para o usuário
      const allMessages = Object.values(globalCheck.errors)
        .flatMap(o => Object.values(o));
      toast.error(`Inconsistência cronológica detectada: ${allMessages[0]}`);
      return;
    }

    // Mostrar dialog de confirmação
    setShowSubmitDialog(true);
  };

  const handleSubmit = async () => {
    try {
      const processedData = { ...formData };

      // Incluir estados separados no payload
      processedData.impediment_times = impedimentTimes || {};
      processedData.impediment_start_times = impedimentStartTimes || {};
      processedData.impediment_comments = impedimentComments || {};
      processedData.selected_materials = selectedMaterials || [];
      processedData.material_actions = materialActions || {};

      // Derivar lista única de atividades a partir das entries (mantém compat com lógica de Seção 6)
      const _uniqueFromEntries = (entries) => {
        const seen = [];
        (entries || []).forEach(e => { if (e?.activity && !seen.includes(e.activity)) seen.push(e.activity); });
        return seen;
      };
      processedData.morning_activities = _uniqueFromEntries(processedData.morning_activity_entries);
      processedData.afternoon_activities = _uniqueFromEntries(processedData.afternoon_activity_entries);

      // Consolidar urgency_comment_<material> em dict dedicado
      const urgencyComments = {};
      Object.keys(processedData).forEach((k) => {
        if (k.startsWith('urgency_comment_')) {
          const matName = k.replace('urgency_comment_', '');
          if (processedData[k]) {
            urgencyComments[matName] = processedData[k];
          }
        }
      });
      processedData.material_urgency_comments = urgencyComments;

      // Converter strings numéricas para números
      const numberFields = [
        'sandblasting_hours_today',
        'columns_sandblasted_entirely', 'sandblasted_columns_width', 'sandblasted_columns_height',
        'partial_sandblasted_height', 'robot_advance_speed', 'turbine_speed',
        'sandblasting_current', 'columns_painted_entirely', 'painted_columns_width', 
        'painted_columns_height', 'partial_painted_width', 'partial_painted_height', 
        'painting_hours', 'sealing_kits_stock', 'grit_used_volume', 'grit_lost_volume',
        'grit_remaining_volume', 'paint_used_volume', 'paint_remaining_volume',
        'component_ab_used_volume', 'component_ab_remaining_volume', 'inhibitor_used_volume',
        'inhibitor_remaining_volume', 'solvent_used_volume', 'solvent_remaining_volume'
      ];

      numberFields.forEach(field => {
        if (processedData[field] === '' || processedData[field] === null || processedData[field] === undefined) {
          processedData[field] = null;
        } else {
          processedData[field] = parseFloat(processedData[field]);
          if (Number.isNaN(processedData[field])) processedData[field] = null;
        }
      });

      if (editingReport) {
        await axios.put(`${API}/reports/${editingReport.id}`, processedData);
        setSubmitSuccess(true);
        
        setTimeout(() => {
          setShowSubmitDialog(false);
          setSubmitSuccess(false);
          toast.success("Relatório atualizado com sucesso!");
          onReportCreated();
        }, 2000);
      } else {
        await axios.post(`${API}/reports`, processedData);
        setSubmitSuccess(true);
        
        // Limpar rascunho após submeter
        const draftKey = `rdo_draft_${formData.report_date}`;
        localStorage.removeItem(draftKey);
        
        setTimeout(() => {
          setShowSubmitDialog(false);
          setSubmitSuccess(false);
          toast.success("Relatório criado com sucesso!");
          onReportCreated();
        }, 2000);
      }
    } catch (error) {
      setShowSubmitDialog(false);
      toast.error(error.response?.data?.detail || "Erro ao salvar relatório");
    }
  };

  // Template básico
  const loadBasicTemplate = () => {
    setFormData(prev => ({
      ...prev,
      morning_activities: ['RDO_ISSUANCE', 'SANDBLASTING'],
      afternoon_activities: ['PAINTING_LAYER1'],
      region_worked: 'CAV-01',
      morning_comments: 'Atividades realizadas conforme programação'
    }));
    toast.success("Template básico carregado");
  };

  return (
    <Card className="w-full max-w-6xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl font-bold text-slate-800">
            {editingReport ? "Editar Relatório RDO" : "Novo Relatório RDO"}
          </CardTitle>
          <div className="flex items-center gap-3">
            {lastSaved && (
              <div className="flex items-center gap-1 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                Salvo: {lastSaved.toLocaleTimeString()}
              </div>
            )}
            {isAutoSaving && (
              <Badge variant="outline" className="text-blue-600">
                <Save className="w-3 h-3 mr-1" />
                Salvando...
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={loadBasicTemplate}
              data-testid="load-template-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              Template Básico
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={saveDraftToBackend}
              disabled={savingDraft}
              data-testid="save-draft-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Cloud className="w-4 h-4 mr-1" />
              {savingDraft ? "Salvando..." : "Salvar Rascunho"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Banner opt-in para retomar rascunho local */}
        {!editingReport && localDraftAvailable && (
          <div
            className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
            data-testid="local-draft-banner"
          >
            <FileText className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                Rascunho local disponível
              </div>
              <div className="text-amber-800/90 text-xs mt-0.5">
                Há um rascunho salvo neste navegador
                {localDraftAvailable.savedAt
                  ? ` (${new Date(localDraftAvailable.savedAt).toLocaleString('pt-BR')})`
                  : ''}.
                Deseja carregá-lo? Caso contrário, este formulário começa em branco.
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleResumeLocalDraft}
                data-testid="local-draft-resume-btn"
                className="border-amber-400"
              >
                Carregar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDiscardLocalDraft}
                data-testid="local-draft-discard-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-9 h-auto">
            {tabs.map((tab) => (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                disabled={!canAccessTab(tab.id)}
                className={`flex flex-col items-center p-2 text-xs relative ${
                  !canAccessTab(tab.id) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <span className="text-lg mb-1">{tab.icon}</span>
                <span className="text-center leading-tight">{tab.label}</span>
                {isTabComplete(tab.id) && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
                {validationErrors[tab.id] && Object.keys(validationErrors[tab.id]).length > 0 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                )}
                {!canAccessTab(tab.id) && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-400 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">🔒</span>
                  </div>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Aba 1: Informações Administrativas */}
          <TabsContent value="admin" className="space-y-6">
            {/* Seletor de Cliente */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <Label htmlFor="client_id" className="text-base font-semibold text-slate-800">
                Cliente * <span className="text-xs font-normal text-slate-600">(a qual cliente este RDO pertence?)</span>
              </Label>
              <Select
                value={formData.client_id || ''}
                onValueChange={(value) => {
                  setFormData({ ...formData, client_id: value });
                  // Limpa o erro reativamente assim que o cliente é escolhido
                  setValidationErrors(prev => {
                    if (!prev?.admin?.client_id) return prev;
                    const next = { ...prev, admin: { ...prev.admin } };
                    delete next.admin.client_id;
                    return next;
                  });
                }}
                disabled={loadingClients}
              >
                <SelectTrigger
                  id="client_id"
                  data-testid="rdo-client-selector"
                  className={`mt-2 bg-white ${validationErrors.admin?.client_id ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder={loadingClients ? 'Carregando clientes...' : 'Selecione um cliente'} />
                </SelectTrigger>
                <SelectContent>
                  {activeClients.length === 0 && !loadingClients ? (
                    <div className="px-3 py-2 text-sm text-slate-500">
                      Nenhum cliente "Em Andamento" cadastrado. Contate um administrador.
                    </div>
                  ) : (
                    activeClients.map((client) => (
                      <SelectItem
                        key={client.id}
                        value={client.id}
                        data-testid={`rdo-client-option-${client.id}`}
                      >
                        {client.client_name}
                        {client.contract_number ? ` — Contrato ${client.contract_number}` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {validationErrors.admin?.client_id && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.admin.client_id}</p>
              )}
              <p className="text-xs text-slate-600 mt-2">
                Apenas clientes com status "Em Andamento" são exibidos.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="report_date">Data do Relatório *</Label>
                <Input
                  id="report_date"
                  type="date"
                  data-testid="report-date-input"
                  value={formData.report_date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className={validationErrors.admin?.report_date ? "border-red-500" : ""}
                />
                {validationErrors.admin?.report_date && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.admin.report_date}</p>
                )}
                <p className="text-sm text-slate-600 mt-1">
                  Não é possível selecionar datas futuras
                </p>
              </div>
              
              <div>
                <Label htmlFor="pt_room_time">Horário na sala de PT</Label>
                <Input
                  id="pt_room_time"
                  type="time"
                  data-testid="pt-room-time-input"
                  value={formData.pt_room_time}
                  onChange={(e) => setFormData({...formData, pt_room_time: e.target.value})}
                />
                <p className="text-sm text-slate-600 mt-1">
                  Primeiro horário da sequência de PT
                </p>
              </div>
              
              <div>
                <Label htmlFor="pt_issuance_time">Horário de emissão da PT</Label>
                <Input
                  id="pt_issuance_time"
                  type="time"
                  data-testid="pt-issuance-time-input"
                  value={formData.pt_issuance_time}
                  onChange={(e) => setFormData({...formData, pt_issuance_time: e.target.value})}
                  min={formData.pt_room_time || undefined}
                  className={validationErrors.admin?.pt_issuance_time ? "border-red-500" : ""}
                />
                {validationErrors.admin?.pt_issuance_time && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.admin.pt_issuance_time}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="pt_numbers">Número das PTs</Label>
                <Input
                  id="pt_numbers"
                  data-testid="pt-numbers-input"
                  placeholder="Ex: PT123, PT456"
                  value={formData.pt_numbers}
                  onChange={(e) => setFormData({...formData, pt_numbers: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="pt_approval_time">Horário de aprovação da PT</Label>
                <Input
                  id="pt_approval_time"
                  type="time"
                  data-testid="pt-approval-time-input"
                  value={formData.pt_approval_time}
                  onChange={(e) => setFormData({...formData, pt_approval_time: e.target.value})}
                  min={formData.pt_issuance_time || undefined}
                  className={validationErrors.admin?.pt_approval_time ? "border-red-500" : ""}
                />
                {validationErrors.admin?.pt_approval_time && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.admin.pt_approval_time}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="morning_start_time">Início das atividades da manhã</Label>
                <Input
                  id="morning_start_time"
                  type="time"
                  data-testid="morning-start-time-input"
                  value={formData.morning_start_time}
                  onChange={(e) => setFormData({...formData, morning_start_time: e.target.value})}
                  min={formData.pt_approval_time || undefined}
                  className={validationErrors.admin?.morning_start_time ? "border-red-500" : ""}
                />
                {validationErrors.admin?.morning_start_time && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.admin.morning_start_time}</p>
                )}
              </div>
            </div>
            <CustomQuestionsSection
              section="admin"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_admin || {}}
            />

          </TabsContent>

          {/* Aba 2: Atividades da Manhã */}
          <TabsContent value="morning" className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Atividades realizadas na MANHÃ *</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Adicione cada ocorrência separadamente. A mesma atividade pode aparecer mais de uma vez.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addActivityEntry('morning_activity_entries')}
                data-testid="morning-add-activity-btn"
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
              >
                <PlusCircle className="w-4 h-4 mr-1" /> Adicionar Atividade
              </Button>
            </div>

            {validationErrors.morning?.morning_activity_entries && (
              <p className="text-red-500 text-sm" data-testid="morning-entries-error">
                {validationErrors.morning.morning_activity_entries}
              </p>
            )}

            {(formData.morning_activity_entries || []).length === 0 ? (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg" data-testid="morning-empty-state">
                Nenhuma atividade adicionada — clique em "Adicionar Atividade" para registrar.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.morning_activity_entries.map((entry, idx) => (
                  <ActivityEntryCard
                    key={entry.id || idx}
                    entry={entry}
                    idx={idx}
                    fieldName="morning_activity_entries"
                    period="morning"
                    activityOptions={activityOptions}
                    onUpdate={updateActivityEntry}
                    onRemove={removeActivityEntry}
                    errors={validationErrors.morning || {}}
                  />
                ))}
              </div>
            )}

            <div>
              <Label htmlFor="morning_comments">Comentários gerais sobre o período da manhã</Label>
              <Textarea
                id="morning_comments"
                data-testid="morning-comments-textarea"
                value={formData.morning_comments}
                onChange={(e) => setFormData({...formData, morning_comments: e.target.value})}
                placeholder="Observações gerais que não pertencem a uma atividade específica…"
              />
            </div>
            <CustomQuestionsSection
              section="morning"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_morning || {}}
            />

          </TabsContent>

          {/* Aba 3: Parada de Almoço */}
          <TabsContent value="lunch" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lunch_departure_time">Horário de SAÍDA para almoço</Label>
                <Input
                  id="lunch_departure_time"
                  type="time"
                  data-testid="lunch-departure-time-input"
                  value={formData.lunch_departure_time}
                  onChange={(e) => setFormData({...formData, lunch_departure_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="lunch_return_time">Horário de VOLTA do almoço</Label>
                <Input
                  id="lunch_return_time"
                  type="time"
                  data-testid="lunch-return-time-input"
                  value={formData.lunch_return_time}
                  onChange={(e) => setFormData({...formData, lunch_return_time: e.target.value})}
                />
              </div>
            </div>
            <CustomQuestionsSection
              section="lunch"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_lunch || {}}
            />

          </TabsContent>

          {/* Aba 4: Atividades da Tarde */}
          <TabsContent value="afternoon" className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-base font-semibold">Atividades realizadas na TARDE *</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Adicione cada ocorrência separadamente. A mesma atividade pode aparecer mais de uma vez.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addActivityEntry('afternoon_activity_entries')}
                data-testid="afternoon-add-activity-btn"
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
              >
                <PlusCircle className="w-4 h-4 mr-1" /> Adicionar Atividade
              </Button>
            </div>

            {validationErrors.afternoon?.afternoon_activity_entries && (
              <p className="text-red-500 text-sm" data-testid="afternoon-entries-error">
                {validationErrors.afternoon.afternoon_activity_entries}
              </p>
            )}

            {(formData.afternoon_activity_entries || []).length === 0 ? (
              <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg" data-testid="afternoon-empty-state">
                Nenhuma atividade adicionada — clique em "Adicionar Atividade" para registrar.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.afternoon_activity_entries.map((entry, idx) => (
                  <ActivityEntryCard
                    key={entry.id || idx}
                    entry={entry}
                    idx={idx}
                    fieldName="afternoon_activity_entries"
                    period="afternoon"
                    activityOptions={activityOptions}
                    onUpdate={updateActivityEntry}
                    onRemove={removeActivityEntry}
                    errors={validationErrors.afternoon || {}}
                  />
                ))}
              </div>
            )}

            <div>
              <Label htmlFor="afternoon_comments">Comentários gerais sobre o período da tarde</Label>
              <Textarea
                id="afternoon_comments"
                data-testid="afternoon-comments-textarea"
                value={formData.afternoon_comments}
                onChange={(e) => setFormData({...formData, afternoon_comments: e.target.value})}
                placeholder="Observações gerais que não pertencem a uma atividade específica…"
              />
            </div>
            <CustomQuestionsSection
              section="afternoon"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_afternoon || {}}
            />

          </TabsContent>

          {/* Aba 5: Fotos e Comentários */}
          <TabsContent value="photos" className="space-y-6">
            <div>
              <Label>Fotos de Acompanhamento de Evolução (até 3 fotos)</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                {[1, 2, 3].map((photoNum) => {
                  const field = `progress_photo_${photoNum}`;
                  const isUploading = uploadingPhoto[field];
                  const previewUrl = photoPreviews[field];
                  const storagePath = formData[field];
                  return (
                    <div key={photoNum} className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                      <Label htmlFor={`photo_${photoNum}`} className="block text-center mb-2">
                        📸 Foto {photoNum}
                      </Label>
                      <Input
                        id={`photo_${photoNum}`}
                        type="file"
                        accept="image/*"
                        data-testid={`progress-photo-${photoNum}-input`}
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) uploadPhoto(field, file);
                        }}
                        className="w-full"
                      />
                      {isUploading && (
                        <p className="text-sm text-blue-600 mt-2">
                          <Save className="w-3 h-3 inline mr-1 animate-pulse" />
                          Enviando...
                        </p>
                      )}
                      {previewUrl && (
                        <img
                          src={previewUrl}
                          alt={`Preview foto ${photoNum}`}
                          className="mt-2 rounded-md max-h-32 w-full object-cover"
                          data-testid={`progress-photo-${photoNum}-preview`}
                        />
                      )}
                      {storagePath && !isUploading && (
                        <p className="text-xs text-green-600 mt-1" data-testid={`progress-photo-${photoNum}-status`}>
                          ✅ Armazenada
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-slate-600 mt-3">
                Formatos aceitos: JPG, PNG, WebP, GIF. Tamanho máximo: 10MB por foto.
              </p>
            </div>
            <CustomQuestionsSection
              section="photos"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_photos || {}}
            />

          </TabsContent>

          {/* Aba 6: Dados Específicos das Atividades */}
          <TabsContent value="activities" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="region_worked">Qual a região (CAV) foi trabalhada hoje? *</Label>
                <Input
                  id="region_worked"
                  data-testid="region-worked-input"
                  placeholder="Ex: CAV-01"
                  value={formData.region_worked}
                  onChange={(e) => setFormData({...formData, region_worked: e.target.value})}
                  className={validationErrors.activities?.region_worked ? "border-red-500" : ""}
                />
                {validationErrors.activities?.region_worked && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.activities.region_worked}</p>
                )}
              </div>
            </div>

            {/* Seção de Jateamento - Condicional */}
            {isSandblastingSelected() && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-slate-800">📌 Dados de Jateamento</h4>
                
                <div>
                  <Label htmlFor="sandblasting_hours_today">Quantas HORAS de execução de jateamento no dia de hoje? *</Label>
                  <Input
                    id="sandblasting_hours_today"
                    type="number"
                    step="0.5"
                    data-testid="sandblasting-hours-today-input"
                    placeholder="Ex: 6.5"
                    value={formData.sandblasting_hours_today}
                    onChange={(e) => setFormData({...formData, sandblasting_hours_today: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="columns_sandblasted_entirely">Quantas colunas foram jateadas por inteiro?</Label>
                    <Input
                      id="columns_sandblasted_entirely"
                      type="number"
                      data-testid="columns-sandblasted-entirely-input"
                      value={formData.columns_sandblasted_entirely}
                      onChange={(e) => setFormData({...formData, columns_sandblasted_entirely: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sandblasted_columns_width">Largura das colunas jateadas (metros)</Label>
                    <Input
                      id="sandblasted_columns_width"
                      type="number"
                      step="0.01"
                      data-testid="sandblasted-columns-width-input"
                      value={formData.sandblasted_columns_width}
                      onChange={(e) => setFormData({...formData, sandblasted_columns_width: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sandblasted_columns_height">Altura das colunas jateadas (metros)</Label>
                    <Input
                      id="sandblasted_columns_height"
                      type="number"
                      step="0.01"
                      data-testid="sandblasted-columns-height-input"
                      value={formData.sandblasted_columns_height}
                      onChange={(e) => setFormData({...formData, sandblasted_columns_height: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sandblasting_current">Corrente utilizada para jateamento (A)</Label>
                    <Input
                      id="sandblasting_current"
                      type="number"
                      step="0.01"
                      data-testid="sandblasting-current-input"
                      value={formData.sandblasting_current}
                      onChange={(e) => setFormData({...formData, sandblasting_current: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="robot_advance_speed">Velocidade de avanço do robô</Label>
                    <Input
                      id="robot_advance_speed"
                      type="number"
                      step="0.01"
                      data-testid="robot-advance-speed-input"
                      value={formData.robot_advance_speed}
                      onChange={(e) => setFormData({...formData, robot_advance_speed: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="turbine_speed">Velocidade das turbinas (RPM)</Label>
                    <Input
                      id="turbine_speed"
                      type="number"
                      step="0.01"
                      data-testid="turbine-speed-input"
                      value={formData.turbine_speed}
                      onChange={(e) => setFormData({...formData, turbine_speed: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Houve mudança na velocidade das turbinas?</Label>
                  <RadioGroup 
                    value={formData.turbine_speed_changed?.toString() || ""} 
                    onValueChange={(value) => setFormData({...formData, turbine_speed_changed: value === "true"})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="turbine_speed_changed_yes" />
                      <Label htmlFor="turbine_speed_changed_yes">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="turbine_speed_changed_no" />
                      <Label htmlFor="turbine_speed_changed_no">Não</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.turbine_speed_changed && (
                  <div>
                    <Label htmlFor="turbine_speed_change_cav">Citar a CAV onde houve mudança</Label>
                    <Input
                      id="turbine_speed_change_cav"
                      data-testid="turbine-speed-change-cav-input"
                      value={formData.turbine_speed_change_cav}
                      onChange={(e) => setFormData({...formData, turbine_speed_change_cav: e.target.value})}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Houve colunas jateadas PARCIALMENTE?</Label>
                  <RadioGroup 
                    value={formData.partial_columns_sandblasted?.toString() || ""} 
                    onValueChange={(value) => setFormData({...formData, partial_columns_sandblasted: value === "true"})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="partial_sandblasted_yes" />
                      <Label htmlFor="partial_sandblasted_yes">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="partial_sandblasted_no" />
                      <Label htmlFor="partial_sandblasted_no">Não</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.partial_columns_sandblasted && (
                  <div>
                    <Label htmlFor="partial_sandblasted_height">Altura das colunas jateadas PARCIALMENTE (metros)</Label>
                    <Input
                      id="partial_sandblasted_height"
                      type="number"
                      step="0.01"
                      data-testid="partial-sandblasted-height-input"
                      value={formData.partial_sandblasted_height}
                      onChange={(e) => setFormData({...formData, partial_sandblasted_height: e.target.value})}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Houve REPASSAGEM de jateamento?</Label>
                  <RadioGroup 
                    value={formData.sandblasting_rework?.toString() || ""} 
                    onValueChange={(value) => setFormData({...formData, sandblasting_rework: value === "true"})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="sandblasting_rework_yes" />
                      <Label htmlFor="sandblasting_rework_yes">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="sandblasting_rework_no" />
                      <Label htmlFor="sandblasting_rework_no">Não</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.sandblasting_rework && (
                  <div>
                    <Label htmlFor="sandblasting_rework_details">Identifique quais CAVs e colunas</Label>
                    <Textarea
                      id="sandblasting_rework_details"
                      data-testid="sandblasting-rework-details-textarea"
                      value={formData.sandblasting_rework_details}
                      onChange={(e) => setFormData({...formData, sandblasting_rework_details: e.target.value})}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Seção de Pintura - Condicional */}
            {isPaintingSelected() && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-slate-800">🎨 Dados de Pintura</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="columns_painted_entirely">Quantas colunas foram pintadas por inteiro?</Label>
                    <Input
                      id="columns_painted_entirely"
                      type="number"
                      data-testid="columns-painted-entirely-input"
                      value={formData.columns_painted_entirely}
                      onChange={(e) => setFormData({...formData, columns_painted_entirely: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="painted_columns_width">Largura das colunas pintadas (metros)</Label>
                    <Input
                      id="painted_columns_width"
                      type="number"
                      step="0.01"
                      data-testid="painted-columns-width-input"
                      value={formData.painted_columns_width}
                      onChange={(e) => setFormData({...formData, painted_columns_width: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="painted_columns_height">Altura das colunas pintadas (metros)</Label>
                    <Input
                      id="painted_columns_height"
                      type="number"
                      step="0.01"
                      data-testid="painted-columns-height-input"
                      value={formData.painted_columns_height}
                      onChange={(e) => setFormData({...formData, painted_columns_height: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="painting_hours">Horas de execução de pintura</Label>
                    <Input
                      id="painting_hours"
                      type="number"
                      step="0.5"
                      data-testid="painting-hours-input"
                      value={formData.painting_hours}
                      onChange={(e) => setFormData({...formData, painting_hours: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Houve colunas pintadas PARCIALMENTE?</Label>
                  <RadioGroup 
                    value={formData.partial_columns_painted?.toString() || ""} 
                    onValueChange={(value) => setFormData({...formData, partial_columns_painted: value === "true"})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="partial_painted_yes" />
                      <Label htmlFor="partial_painted_yes">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="partial_painted_no" />
                      <Label htmlFor="partial_painted_no">Não</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.partial_columns_painted && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="partial_painted_width">Largura das colunas pintadas PARCIALMENTE (metros)</Label>
                      <Input
                        id="partial_painted_width"
                        type="number"
                        step="0.01"
                        data-testid="partial-painted-width-input"
                        value={formData.partial_painted_width}
                        onChange={(e) => setFormData({...formData, partial_painted_width: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="partial_painted_height">Altura das colunas pintadas PARCIALMENTE (metros)</Label>
                      <Input
                        id="partial_painted_height"
                        type="number"
                        step="0.01"
                        data-testid="partial-painted-height-input"
                        value={formData.partial_painted_height}
                        onChange={(e) => setFormData({...formData, partial_painted_height: e.target.value})}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Seção de Troca de Implemento - Condicional */}
            {shouldEnableImplementChange() && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-slate-800">🔧 Troca de Implemento</h4>
                
                <div>
                  <Label>Houve TROCA DE IMPLEMENTO hoje?</Label>
                  <RadioGroup 
                    value={formData.implement_change?.toString() || ""} 
                    onValueChange={(value) => setFormData({...formData, implement_change: value === "true"})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="implement_change_yes" />
                      <Label htmlFor="implement_change_yes">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="implement_change_no" />
                      <Label htmlFor="implement_change_no">Não</Label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.implement_change && (
                  <div>
                    <Label htmlFor="tool_change_time">Tempo de troca de implemento (minutos)</Label>
                    <Input
                      id="tool_change_time"
                      type="number"
                      data-testid="tool-change-time-input"
                      value={formData.tool_change_time}
                      onChange={(e) => setFormData({...formData, tool_change_time: e.target.value})}
                    />
                  </div>
                )}
              </div>
            )}
            <CustomQuestionsSection
              section="activities"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_activities || {}}
            />

          </TabsContent>

          {/* Aba 7: Impactos & Impedimentos */}
          <TabsContent value="impediments" className="space-y-6">
            <div>
              <Label>Houveram IMPEDIMENTOS que impactaram na execução das atividades hoje?</Label>
              <RadioGroup 
                value={formData.impediments_occurred?.toString() || ""} 
                onValueChange={(value) => setFormData({...formData, impediments_occurred: value === "true"})}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="impediments_yes" data-testid="impediments-yes-radio" />
                  <Label htmlFor="impediments_yes">Sim</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="impediments_no" data-testid="impediments-no-radio" />
                  <Label htmlFor="impediments_no">Não</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.impediments_occurred && (
              <div className="space-y-4">
                <div>
                  <Label>Aponte os IMPEDIMENTOS ocorridos:</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                    {impedimentOptions.map(option => (
                      <div key={`impediment_${option.value}`} className="flex items-center space-x-2">
                        <Checkbox
                          id={`impediment_${option.value}`}
                          data-testid={`impediment-${option.value}`}
                          checked={formData.impediments_list.includes(option.value)}
                          onCheckedChange={(checked) => {
                            handleImpedimentChange(option.value, checked);
                            if (!checked) {
                              // Remove dados associados ao impedimento desmarcado
                              const removeKey = (obj, key) => {
                                const next = { ...obj };
                                delete next[key];
                                return next;
                              };
                              setImpedimentTimes((prev) => removeKey(prev, option.value));
                              setImpedimentStartTimes((prev) => removeKey(prev, option.value));
                              setImpedimentComments((prev) => removeKey(prev, option.value));
                            }
                          }}
                        />
                        <Label htmlFor={`impediment_${option.value}`} className="text-sm">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cards individuais por impedimento: Início → Duração → Comentário */}
                {formData.impediments_list.length > 0 && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-700">
                      📝 Detalhes de cada impedimento
                    </h5>
                    {formData.impediments_list.map((impediment) => {
                      const optLabel = impedimentOptions.find(opt => opt.value === impediment)?.label;
                      const isOther = impediment === 'OTHER';
                      const commentError = validationErrors.impediments?.[`comment_${impediment}`];
                      return (
                        <div
                          key={`detail_${impediment}`}
                          className="border rounded-lg p-4 bg-amber-50 border-amber-200 space-y-3"
                          data-testid={`impediment-card-${impediment}`}
                        >
                          <div className="font-semibold text-amber-900">{optLabel}</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label htmlFor={`start_${impediment}`} className="text-xs">
                                ⏰ Horário de início
                              </Label>
                              <Input
                                id={`start_${impediment}`}
                                type="time"
                                data-testid={`impediment-start-${impediment}`}
                                value={impedimentStartTimes[impediment] || ''}
                                onChange={(e) => setImpedimentStartTimes(prev => ({
                                  ...prev,
                                  [impediment]: e.target.value
                                }))}
                                className="bg-white"
                              />
                            </div>
                            <div>
                              <Label htmlFor={`time_${impediment}`} className="text-xs">
                                ⏱️ Duração (HH:MM)
                              </Label>
                              <Input
                                id={`time_${impediment}`}
                                type="time"
                                data-testid={`impediment-time-${impediment}`}
                                value={impedimentTimes[impediment] || ''}
                                onChange={(e) => setImpedimentTimes(prev => ({
                                  ...prev,
                                  [impediment]: e.target.value
                                }))}
                                className="bg-white"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor={`comment_${impediment}`} className="text-xs">
                              💬 Comentário {isOther ? '*' : '(opcional)'}
                            </Label>
                            <Textarea
                              id={`comment_${impediment}`}
                              data-testid={`impediment-comment-${impediment}`}
                              value={impedimentComments[impediment] || ''}
                              onChange={(e) => setImpedimentComments(prev => ({
                                ...prev,
                                [impediment]: e.target.value
                              }))}
                              placeholder={isOther
                                ? 'Descreva este impedimento (obrigatório)'
                                : 'Observações sobre este evento (opcional)'}
                              className={`bg-white ${commentError ? 'border-red-500' : ''}`}
                              rows={2}
                            />
                            {commentError && (
                              <p className="text-red-500 text-xs mt-1">{commentError}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Total acumulado — calculado em tempo real */}
                    {(() => {
                      let total = 0;
                      for (const t of Object.values(impedimentTimes)) {
                        if (!t || typeof t !== 'string' || !t.includes(':')) continue;
                        const [h, m] = t.split(':').map(Number);
                        if (!Number.isNaN(h) && !Number.isNaN(m)) total += h * 60 + m;
                      }
                      const hh = String(Math.floor(total / 60)).padStart(2, '0');
                      const mm = String(total % 60).padStart(2, '0');
                      return (
                        <div
                          className="border rounded-lg p-3 bg-slate-100 flex items-center justify-between"
                          data-testid="impediment-total-display"
                        >
                          <span className="font-medium text-slate-700">
                            🧮 Total acumulado em impedimentos
                          </span>
                          <span className="font-mono text-lg font-bold text-slate-900" data-testid="impediment-total-value">
                            {hh}:{mm}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div>
                  <Label htmlFor="impediments_comments">Comentários gerais sobre os impedimentos do dia</Label>
                  <Textarea
                    id="impediments_comments"
                    data-testid="impediments-comments-textarea"
                    value={formData.impediments_comments}
                    onChange={(e) => setFormData({...formData, impediments_comments: e.target.value})}
                    placeholder="Resumo, contexto adicional ou ações tomadas"
                  />
                </div>
              </div>
            )}
            <CustomQuestionsSection
              section="impediments"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_impediments || {}}
            />

          </TabsContent>

          {/* Aba 8: Almoxarifado & Spare Parts */}
          <TabsContent value="warehouse" className="space-y-6">
            {/* Movimento de Almoxarifado */}
            <div>
              <Label>Houve movimento de qualquer item do ALMOXARIFADO?</Label>
              <RadioGroup 
                value={formData.warehouse_item_moved?.toString() || ""} 
                onValueChange={(value) => setFormData({...formData, warehouse_item_moved: value === "true"})}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="warehouse_moved_yes" />
                  <Label htmlFor="warehouse_moved_yes">Sim</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="warehouse_moved_no" />
                  <Label htmlFor="warehouse_moved_no">Não</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.warehouse_item_moved && (
              <div className="space-y-4">
                <div>
                  <Label>📦 Lista de Materiais em Estoque Embarcado</Label>
                  {(() => {
                    const currentClient = activeClients.find(c => c.id === formData.client_id);
                    const catalogItems = (currentClient?.warehouse_items || []).map(it => it.name).filter(Boolean);
                    const catalogUnitByName = Object.fromEntries(
                      (currentClient?.warehouse_items || [])
                        .filter(it => it.name)
                        .map(it => [it.name, it.unit])
                    );
                    const hasCatalog = catalogItems.length > 0;

                    // Combinar: itens do catálogo (ordenados) + já selecionados que são ad-hoc (não estão no catálogo)
                    const adhocSelected = selectedMaterials.filter(m => !catalogItems.includes(m));
                    const displayList = [...catalogItems, ...adhocSelected];

                    return (
                      <>
                        {hasCatalog ? (
                          <p className="text-sm text-slate-600 mb-3">
                            Itens cadastrados para <strong>{currentClient.client_name}</strong>.
                            Se precisar registrar algo fora da lista, use "Adicionar item não cadastrado".
                          </p>
                        ) : (
                          <div
                            className="text-sm bg-amber-50 border border-amber-200 rounded p-2 mb-3"
                            data-testid="material-catalog-empty-warning"
                          >
                            ⚠️ Nenhum item de almoxarifado cadastrado para este cliente.
                            Registre abaixo o material usando o campo "Adicionar item não cadastrado"
                            (aparecerão como "pendentes de cadastro" no Dashboard).
                          </div>
                        )}

                        {displayList.length > 0 && (
                          <div className="max-h-60 overflow-y-auto border rounded-lg p-3 space-y-2">
                            {displayList.map((material, index) => {
                              const isAdhoc = !catalogItems.includes(material);
                              const unit = catalogUnitByName[material];
                              return (
                                <div key={material} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`material_${index}`}
                                    data-testid={`material-${index}`}
                                    checked={selectedMaterials.includes(material)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setSelectedMaterials(prev => [...prev, material]);
                                      } else {
                                        setSelectedMaterials(prev => prev.filter(m => m !== material));
                                        const newActions = { ...materialActions };
                                        delete newActions[material];
                                        setMaterialActions(newActions);
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`material_${index}`} className="text-sm flex-1">
                                    {material}
                                    {unit && (
                                      <span className="text-xs text-slate-500 ml-1">({unit})</span>
                                    )}
                                    {isAdhoc && (
                                      <Badge variant="outline" className="ml-2 text-xs text-amber-700 border-amber-400">
                                        pendente de cadastro
                                      </Badge>
                                    )}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Adicionar material ad-hoc */}
                        <div className="mt-3 flex gap-2">
                          <Input
                            placeholder="Adicionar item não cadastrado..."
                            data-testid="adhoc-material-input"
                            value={adhocMaterialInput}
                            onChange={(e) => setAdhocMaterialInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const v = adhocMaterialInput.trim();
                                if (!v) return;
                                if (selectedMaterials.includes(v) || catalogItems.includes(v)) {
                                  toast.error('Item já está na lista');
                                } else {
                                  setSelectedMaterials(prev => [...prev, v]);
                                  setAdhocMaterialInput('');
                                }
                              }
                            }}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            data-testid="adhoc-material-add-btn"
                            onClick={() => {
                              const v = adhocMaterialInput.trim();
                              if (!v) return;
                              if (selectedMaterials.includes(v) || catalogItems.includes(v)) {
                                toast.error('Item já está na lista');
                              } else {
                                setSelectedMaterials(prev => [...prev, v]);
                                setAdhocMaterialInput('');
                              }
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Adicionar
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Ações para materiais selecionados */}
                {selectedMaterials.length > 0 && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-slate-700">🔧 Ação para cada material:</h5>
                    {selectedMaterials.map((material, index) => (
                      <div key={material} className="border rounded-lg p-3 space-y-2">
                        <Label className="font-medium text-sm">{material}</Label>
                        <RadioGroup 
                          value={materialActions[material] || ""} 
                          onValueChange={(value) => setMaterialActions(prev => ({
                            ...prev,
                            [material]: value
                          }))}
                        >
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="sucatear" id={`sucatear_${index}`} />
                              <Label htmlFor={`sucatear_${index}`} className="text-sm">Sucatear</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="remanufaturar" id={`remanufaturar_${index}`} />
                              <Label htmlFor={`remanufaturar_${index}`} className="text-sm">Remanufaturar</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="repor" id={`repor_${index}`} />
                              <Label htmlFor={`repor_${index}`} className="text-sm">Repor</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="repor_urgencia" id={`repor_urgencia_${index}`} />
                              <Label htmlFor={`repor_urgencia_${index}`} className="text-sm">Repor com Urgência</Label>
                            </div>
                          </div>
                        </RadioGroup>

                        {/* Campo para "Repor com Urgência" */}
                        {materialActions[material] === 'repor_urgencia' && (
                          <div className="mt-2">
                            <Label htmlFor={`urgency_comment_${index}`}>Comentar sobre "Repor com Urgência" *</Label>
                            <Textarea
                              id={`urgency_comment_${index}`}
                              data-testid={`urgency-comment-${index}`}
                              value={formData[`urgency_comment_${material}`] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                [`urgency_comment_${material}`]: e.target.value
                              }))}
                              placeholder="Justifique a urgência da reposição..."
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <Label htmlFor="warehouse_general_comments">Comentários gerais sobre movimentação</Label>
                  <Textarea
                    id="warehouse_general_comments"
                    data-testid="warehouse-general-comments-textarea"
                    value={formData.warehouse_general_comments}
                    onChange={(e) => setFormData({...formData, warehouse_general_comments: e.target.value})}
                    placeholder="Observações sobre a movimentação de materiais..."
                  />
                </div>
              </div>
            )}

            {/* Kit Turbina */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-slate-800">⚙️ Kit Turbina</h4>
              
              <div>
                <Label>Houve necessidade de troca de Kit Turbina?</Label>
                <RadioGroup 
                  value={formData.turbine_kit_replaced?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, turbine_kit_replaced: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="turbine_kit_yes" />
                    <Label htmlFor="turbine_kit_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="turbine_kit_no" />
                    <Label htmlFor="turbine_kit_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.turbine_kit_replaced && (
                <div className="space-y-4">
                  {/* Seleção do kit trocado */}
                  <div>
                    <Label>Qual kit foi trocado?</Label>
                    <RadioGroup 
                      value={formData.turbine_kit_position || ""} 
                      onValueChange={(value) => setFormData({...formData, turbine_kit_position: value})}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="direito" id="kit_direito" />
                        <Label htmlFor="kit_direito">Direito</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="esquerdo" id="kit_esquerdo" />
                        <Label htmlFor="kit_esquerdo">Esquerdo</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="ambos" id="kit_ambos" />
                        <Label htmlFor="kit_ambos">Ambos</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label>O novo kit foi BALANCEADO antes da montagem?</Label>
                    <RadioGroup 
                      value={formData.turbine_kit_balanced?.toString() || ""} 
                      onValueChange={(value) => setFormData({...formData, turbine_kit_balanced: value === "true"})}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="true" id="turbine_balanced_yes" />
                        <Label htmlFor="turbine_balanced_yes">Sim</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="false" id="turbine_balanced_no" />
                        <Label htmlFor="turbine_balanced_no">Não</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Registro fotográfico do processo */}
                  <div>
                    <Label>📸 Registro fotográfico do processo de troca</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                      {[
                        { field: 'kit_photo_removed', label: '📷 Foto 01: Kit Retirado', testid: 'kit-photo-removed-input' },
                        { field: 'kit_photo_replaced', label: '📷 Foto 02: Kit Trocado', testid: 'kit-photo-replaced-input' },
                        { field: 'kit_photo_balanced', label: '📷 Foto 03: Kit Balanceado', testid: 'kit-photo-balanced-input' },
                      ].map(({ field, label, testid }) => {
                        const isUploading = uploadingPhoto[field];
                        const previewUrl = photoPreviews[field];
                        const storagePath = formData[field];
                        return (
                          <div key={field} className="border-2 border-dashed border-slate-300 rounded-lg p-3">
                            <Label htmlFor={field} className="block text-center mb-2 text-sm">{label}</Label>
                            <Input
                              id={field}
                              type="file"
                              accept="image/*"
                              data-testid={testid}
                              disabled={isUploading}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) uploadPhoto(field, file);
                              }}
                            />
                            {isUploading && (
                              <p className="text-sm text-blue-600 mt-2">
                                <Save className="w-3 h-3 inline mr-1 animate-pulse" />
                                Enviando...
                              </p>
                            )}
                            {previewUrl && (
                              <img
                                src={previewUrl}
                                alt={label}
                                className="mt-2 rounded-md max-h-28 w-full object-cover"
                                data-testid={`${field.replace(/_/g, '-')}-preview`}
                              />
                            )}
                            {storagePath && !isUploading && (
                              <p className="text-xs text-green-600 mt-1" data-testid={`${field.replace(/_/g, '-')}-status`}>
                                ✅ Armazenada
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Comentários e justificativa */}
                  <div>
                    <Label htmlFor="turbine_kit_justification">Comente e justifique a troca de kits *</Label>
                    <Textarea
                      id="turbine_kit_justification"
                      data-testid="turbine-kit-justification-textarea"
                      value={formData.turbine_kit_justification}
                      onChange={(e) => setFormData({...formData, turbine_kit_justification: e.target.value})}
                      placeholder="Descreva o motivo da troca, condições do kit antigo, etc..."
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Kit de Vedação */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-slate-800">🔧 Kit de Vedação</h4>
              
              <div>
                <Label>Houve troca de Kits de vedação?</Label>
                <RadioGroup 
                  value={formData.sealing_kit_replaced?.toString() || ""} 
                  onValueChange={(value) => setFormData({...formData, sealing_kit_replaced: value === "true"})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="sealing_kit_yes" />
                    <Label htmlFor="sealing_kit_yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="sealing_kit_no" />
                    <Label htmlFor="sealing_kit_no">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="sealing_kits_stock">Quantos kits de vedação temos no almoxarifado?</Label>
                <Input
                  id="sealing_kits_stock"
                  type="number"
                  data-testid="sealing-kits-stock-input"
                  value={formData.sealing_kits_stock}
                  onChange={(e) => setFormData({...formData, sealing_kits_stock: e.target.value})}
                />
              </div>
            </div>

            {/* Controle de Materiais - Condicional */}
            {(isSandblastingSelected() || isPaintingSelected() || isInhibitorSelected()) && (
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-slate-800">📊 Controle de Materiais</h4>
                
                {isSandblastingSelected() && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-700">Abrasivo (Granalha)</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="grit_used_volume">Volume utilizado (kg)</Label>
                        <Input
                          id="grit_used_volume"
                          type="number"
                          step="0.01"
                          data-testid="grit-used-volume-input"
                          value={formData.grit_used_volume}
                          onChange={(e) => setFormData({...formData, grit_used_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="grit_remaining_volume">Volume restante (kg)</Label>
                        <Input
                          id="grit_remaining_volume"
                          type="number"
                          step="0.01"
                          data-testid="grit-remaining-volume-input"
                          value={formData.grit_remaining_volume}
                          onChange={(e) => setFormData({...formData, grit_remaining_volume: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Houve perda de abrasivo?</Label>
                      <RadioGroup 
                        value={formData.grit_loss?.toString() || ""} 
                        onValueChange={(value) => setFormData({...formData, grit_loss: value === "true"})}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="grit_loss_yes" />
                          <Label htmlFor="grit_loss_yes">Sim</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="grit_loss_no" />
                          <Label htmlFor="grit_loss_no">Não</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {formData.grit_loss && (
                      <div>
                        <Label htmlFor="grit_lost_volume">Quantidade perdida (kg)</Label>
                        <Input
                          id="grit_lost_volume"
                          type="number"
                          step="0.01"
                          data-testid="grit-lost-volume-input"
                          value={formData.grit_lost_volume}
                          onChange={(e) => setFormData({...formData, grit_lost_volume: e.target.value})}
                        />
                      </div>
                    )}

                    <div>
                      <Label>Atingimos estoque mínimo de 2.000 kg?</Label>
                      <RadioGroup 
                        value={formData.grit_minimum_reached?.toString() || ""} 
                        onValueChange={(value) => {
                          setFormData({...formData, grit_minimum_reached: value === "true"});
                          if (value === "true") {
                            toast.warning("⚠️ Estoque mínimo atingido! Alerta gerado para administradores.");
                          }
                        }}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="grit_minimum_yes" />
                          <Label htmlFor="grit_minimum_yes">Sim</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="grit_minimum_no" />
                          <Label htmlFor="grit_minimum_no">Não</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <Label htmlFor="grit_stock_comments">Comentários sobre o estoque</Label>
                      <Textarea
                        id="grit_stock_comments"
                        data-testid="grit-stock-comments-textarea"
                        value={formData.grit_stock_comments}
                        onChange={(e) => setFormData({...formData, grit_stock_comments: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                {isPaintingSelected() && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-700">Materiais de Pintura</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="paint_used_volume">Tinta utilizada (L)</Label>
                        <Input
                          id="paint_used_volume"
                          type="number"
                          step="0.01"
                          data-testid="paint-used-volume-input"
                          value={formData.paint_used_volume}
                          onChange={(e) => setFormData({...formData, paint_used_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="paint_remaining_volume">Tinta restante (L)</Label>
                        <Input
                          id="paint_remaining_volume"
                          type="number"
                          step="0.01"
                          data-testid="paint-remaining-volume-input"
                          value={formData.paint_remaining_volume}
                          onChange={(e) => setFormData({...formData, paint_remaining_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="component_ab_used_volume">Componente A+B utilizado (L)</Label>
                        <Input
                          id="component_ab_used_volume"
                          type="number"
                          step="0.01"
                          data-testid="component-ab-used-volume-input"
                          value={formData.component_ab_used_volume}
                          onChange={(e) => setFormData({...formData, component_ab_used_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="component_ab_remaining_volume">Componente A+B restante (L)</Label>
                        <Input
                          id="component_ab_remaining_volume"
                          type="number"
                          step="0.01"
                          data-testid="component-ab-remaining-volume-input"
                          value={formData.component_ab_remaining_volume}
                          onChange={(e) => setFormData({...formData, component_ab_remaining_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="solvent_used_volume">Solvente utilizado (L)</Label>
                        <Input
                          id="solvent_used_volume"
                          type="number"
                          step="0.01"
                          data-testid="solvent-used-volume-input"
                          value={formData.solvent_used_volume}
                          onChange={(e) => setFormData({...formData, solvent_used_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="solvent_remaining_volume">Solvente restante (L)</Label>
                        <Input
                          id="solvent_remaining_volume"
                          type="number"
                          step="0.01"
                          data-testid="solvent-remaining-volume-input"
                          value={formData.solvent_remaining_volume}
                          onChange={(e) => setFormData({...formData, solvent_remaining_volume: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {isInhibitorSelected() && (
                  <div className="space-y-3">
                    <h5 className="font-medium text-slate-700">Inibidor de Corrosão</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="inhibitor_used_volume">Inibidor utilizado (L)</Label>
                        <Input
                          id="inhibitor_used_volume"
                          type="number"
                          step="0.01"
                          data-testid="inhibitor-used-volume-input"
                          value={formData.inhibitor_used_volume}
                          onChange={(e) => setFormData({...formData, inhibitor_used_volume: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="inhibitor_remaining_volume">Inibidor restante (L)</Label>
                        <Input
                          id="inhibitor_remaining_volume"
                          type="number"
                          step="0.01"
                          data-testid="inhibitor-remaining-volume-input"
                          value={formData.inhibitor_remaining_volume}
                          onChange={(e) => setFormData({...formData, inhibitor_remaining_volume: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <CustomQuestionsSection
              section="warehouse"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_warehouse || {}}
            />

          </TabsContent>

          {/* Aba 9: Encerramento */}
          <TabsContent value="closing" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="daily_end_time">Horário de ENCERRAMENTO das atividades</Label>
                <Input
                  id="daily_end_time"
                  type="time"
                  data-testid="daily-end-time-input"
                  value={formData.daily_end_time}
                  onChange={(e) => setFormData({...formData, daily_end_time: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="administrative_demands">Campo livre para observações administrativas</Label>
              <Textarea
                id="administrative_demands"
                data-testid="administrative-demands-textarea"
                value={formData.administrative_demands}
                onChange={(e) => setFormData({...formData, administrative_demands: e.target.value})}
                placeholder="Demandas especiais, comunicações importantes, observações gerais..."
                rows={4}
              />
              <p className="text-sm text-slate-600 mt-1">
                Use este espaço para registrar demandas administrativas, comunicações importantes ou qualquer observação relevante sobre o dia.
              </p>
            </div>

            {/* Resumo do Relatório */}
            <div className="border rounded-lg p-4 bg-slate-50">
              <h4 className="font-semibold text-slate-800 mb-3">📋 Resumo do Relatório</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Data:</strong> {formData.report_date || "Não informada"}
                </div>
                <div>
                  <strong>Região (CAV):</strong> {formData.region_worked || "Não informada"}
                </div>
                <div>
                  <strong>Atividades Manhã:</strong> {formData.morning_activities.length} selecionadas
                </div>
                <div>
                  <strong>Atividades Tarde:</strong> {formData.afternoon_activities.length} selecionadas
                </div>
                <div>
                  <strong>Impedimentos:</strong> {formData.impediments_occurred ? "Sim" : formData.impediments_occurred === false ? "Não" : "Não informado"}
                </div>
                <div>
                  <strong>Mov. Almoxarifado:</strong> {formData.warehouse_item_moved ? "Sim" : formData.warehouse_item_moved === false ? "Não" : "Não informado"}
                </div>
              </div>
            </div>
            <CustomQuestionsSection
              section="closing"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_closing || {}}
            />
            <CustomQuestionsSection
              section="demands"
              questions={customQuestions}
              answers={formData.custom_answers}
              onAnswerChange={setCustomAnswer}
              errors={validationErrors.cq_demands || {}}
            />

          </TabsContent>
          
          {/* Dialog para editar relatório existente */}
          {showEditExistingDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-existing-dialog">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <CardTitle className="text-center">⚠️ Relatório Existente</CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p>Já existe um relatório para a data <strong>{formData.report_date}</strong>.</p>
                  <p>Deseja editar o relatório existente?</p>
                  
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      onClick={() => setShowEditExistingDialog(false)}
                      data-testid="continue-new-btn"
                    >
                      Não, continuar novo
                    </Button>
                    <Button
                      onClick={goToMyReports}
                      data-testid="edit-existing-btn"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Sim, editar existente
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={prevTab}
              disabled={activeTab === 'admin'}
              data-testid="prev-tab-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>

            <div className="text-sm text-slate-600">
              Aba {tabs.findIndex(tab => tab.id === activeTab) + 1} de {tabs.length}
            </div>

            {activeTab === 'closing' ? (
              <Button
                onClick={handleSubmitClick}
                data-testid="submit-report-btn"
                className="bg-green-600 hover:bg-green-700"
              >
                {editingReport ? "Atualizar Relatório" : "Criar Relatório"}
              </Button>
            ) : (
              <Button
                onClick={nextTab}
                data-testid="next-tab-btn"
              >
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </Tabs>
      </CardContent>

      {/* Submission Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              {/* Company Logo */}
              <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                <div className="text-white text-center">
                  <FileText className="w-12 h-12 mx-auto mb-1" />
                  <div className="text-xs font-bold">RDO</div>
                </div>
              </div>
            </div>
            <DialogTitle className="text-center text-xl">
              {submitSuccess ? "Sucesso!" : "Confirmar Submissão"}
            </DialogTitle>
            <DialogDescription className="text-center">
              {submitSuccess ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  <CheckCircle2 className="w-16 h-16 text-green-500" />
                  <span className="text-lg font-medium text-slate-800">
                    Relatório {editingReport ? "atualizado" : "criado"} com sucesso!
                  </span>
                  <span className="text-sm text-slate-600">
                    Redirecionando...
                  </span>
                </div>
              ) : (
                <div className="space-y-3 py-4">
                  <p className="text-slate-700">
                    Você está prestes a {editingReport ? "atualizar" : "submeter"} o relatório RDO para a data:
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="font-bold text-blue-900">
                      {new Date(formData.report_date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    {formData.region_worked && (
                      <p className="text-sm text-blue-700 mt-1">
                        Região: {formData.region_worked}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 italic">
                    Deseja continuar?
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {!submitSuccess && (
            <DialogFooter className="sm:justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSubmitDialog(false)}
                data-testid="cancel-submit-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                data-testid="confirm-submit-btn"
                className="bg-green-600 hover:bg-green-700"
              >
                Confirmar Submissão
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RDOFormNew;